import { Box, type SxProps, type Theme } from '@mui/material';
import { getIsProductionalizeMobileFirstSummaryTableActive } from '@repo/hooks/featureFlags/featureFlags';
import type { StateAbbreviation } from '@repo/supergraph/marketplace/types';
import type { SummaryTableRowType } from '@repo/supergraph/roundup/base/types';
import { filterEntitiesByState } from '@repo/ui/utils/geo-data';
import type { PromotionalBannerSignUpOptions } from '../../../../PromotionalBanner/types';
import { SummaryTable } from '../index';
import { MobileFirstSummaryTable } from '../MobileFirstSummaryTable';
import { DEFAULT_HEADINGS, getHeadings } from '../utils';

export type SummaryTableHeadingProps = {
  label: string;
  labelTooltip?: string;
  path?: string;
  isRangeField?: boolean;
};

export type SummaryTableSectionProps = {
  rows: SummaryTableRowType[];
  regionCode?: StateAbbreviation;
  maxRows?: number;
  numTruncatedRows?: number;
  allowTruncatingRows?: boolean;
  slug?: string;
  showSkeleton?: boolean;
  pageUrl: string;
  isAccordionActiveButtonActive?: boolean;
  isFilterActive?: boolean;
  allowTruncationWithFilters?: boolean;
  isFilterLoading?: boolean;
  shouldApplyStylingUpdates?: boolean;
  suppressApyTermPrefix?: boolean;
  containerSx?: SxProps<Theme>;
  shouldDisplayPromoButton?: boolean;
  signUpOptions?: PromotionalBannerSignUpOptions;
};

export const SummaryTableSection = ({
  rows,
  signUpOptions,
  shouldDisplayPromoButton = false,
  regionCode,
  maxRows,
  numTruncatedRows,
  allowTruncatingRows,
  slug,
  showSkeleton,
  pageUrl,
  isAccordionActiveButtonActive,
  isFilterActive,
  allowTruncationWithFilters,
  isFilterLoading,
  shouldApplyStylingUpdates = false,
  suppressApyTermPrefix = false,
  containerSx,
}: SummaryTableSectionProps) => {
  if (!rows?.length) {
    return null;
  }
  const rowsWithDrivers = regionCode
    ? filterEntitiesByState<SummaryTableRowType>(rows, regionCode)
    : rows;

  const headings = rowsWithDrivers[0]
    ? getHeadings(rowsWithDrivers[0].marketplaceEntity)
    : DEFAULT_HEADINGS;

  const shouldShowMobileFirstSummaryTable = getIsProductionalizeMobileFirstSummaryTableActive(
    slug || '',
  );

  const SummaryTableComponent = (
    <Box sx={{ marginX: { xs: 2, md: 0 } }}>
      <SummaryTable
        maxRows={maxRows}
        rows={rowsWithDrivers}
        headings={headings}
        numTruncatedRows={numTruncatedRows}
        allowTruncatingRows={allowTruncatingRows}
        showSkeleton={showSkeleton || isFilterLoading}
        slug={slug}
        pageUrl={pageUrl}
        isAccordionActiveButtonActive={isAccordionActiveButtonActive}
        isFilterActive={isFilterActive}
        allowTruncationWithFilters={allowTruncationWithFilters}
        shouldApplyStylingUpdates={shouldApplyStylingUpdates}
        suppressApyTermPrefix={suppressApyTermPrefix}
        shouldDisplayPromoButton={shouldDisplayPromoButton}
        signUpOptions={signUpOptions}
      />
    </Box>
  );

  return (
    <Box
      sx={
        [
          {
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            maxWidth: { xs: '100%', md: '82%' },
            margin: 'auto',
            marginBottom: { xs: 5, md: 8 },
            marginTop: isFilterActive ? 3 : 5,
          },
          containerSx ?? {},
        ] as SxProps<Theme>
      }
    >
      {shouldShowMobileFirstSummaryTable ? (
        <>
          <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
            <MobileFirstSummaryTable
              maxRows={maxRows}
              rows={rowsWithDrivers}
              numTruncatedRows={numTruncatedRows}
              allowTruncatingRows={allowTruncatingRows}
              showSkeleton={showSkeleton}
              isAccordionActiveButtonActive={isAccordionActiveButtonActive}
            />
          </Box>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>{SummaryTableComponent}</Box>
        </>
      ) : (
        SummaryTableComponent
      )}
    </Box>
  );
};
